import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinRebusSession } from '../../../services/rebusTeamService';
import { getRebus } from '../../../services/rebusService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';

/**
 * Multi-step join flow for rebus teams.
 * Step 1: Enter 6-digit PIN
 * Step 2: Select team (dropdown if predefined, or free text) + enter member name
 */
export default function RebusJoinScreen({ onJoin, prefilledPin = '' }) {
  const navigate = useNavigate();
  const isValidPin = prefilledPin && /^[A-Z0-9]{6}$/i.test(prefilledPin);
  const [step, setStep] = useState(isValidPin ? 'name' : 'pin');
  const [pin, setPin] = useState(isValidPin ? prefilledPin : '');
  const [teamName, setTeamName] = useState('');
  const [memberName, setMemberName] = useState('');
  const [predefinedTeams, setPredefinedTeams] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);

  // When step transitions to 'name', fetch session data to check for predefined teams
  useEffect(() => {
    if (step !== 'name' || !pin) return;

    let cancelled = false;
    setLoadingSession(true);

    (async () => {
      try {
        const sessionRef = doc(db, 'rebusSessions', pin);
        const sessionSnap = await getDoc(sessionRef);
        if (!sessionSnap.exists() || cancelled) return;

        const sessionData = sessionSnap.data();
        const rebusData = await getRebus(sessionData.rebusId);

        if (!cancelled && rebusData.predefinedTeams?.length > 0) {
          setPredefinedTeams(rebusData.predefinedTeams);
        }
      } catch {
        // Ignore — will fail on submit if session doesn't exist
      } finally {
        if (!cancelled) setLoadingSession(false);
      }
    })();

    return () => { cancelled = true; };
  }, [step, pin]);

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 6) {
      setPin(value);
      setError('');
    }
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (!/^[0-9]{6}$/.test(pin.trim())) {
      setError('PIN må vere nøyaktig 6 siffer');
      return;
    }
    setStep('name');
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedTeam = teamName.trim();
    const trimmedMember = memberName.trim();

    if (trimmedTeam.length < 2 || trimmedTeam.length > 30) {
      setError('Lagnamn må vere mellom 2 og 30 teikn');
      return;
    }

    if (trimmedMember.length < 2 || trimmedMember.length > 30) {
      setError('Skriv inn namnet ditt (minst 2 teikn)');
      return;
    }

    setLoading(true);

    try {
      const team = await joinRebusSession(pin, trimmedTeam, trimmedMember);
      onJoin({ sessionId: pin, teamId: team.id });
    } catch (err) {
      // Check if this PIN belongs to a quiz session instead
      try {
        const quizRef = doc(db, 'sessions', pin);
        const quizSnap = await getDoc(quizRef);
        if (quizSnap.exists()) {
          navigate(`/join?session=${pin}`);
          return;
        }
      } catch {
        // Ignore lookup error
      }

      const msg = err.message || '';
      if (msg.includes('Fann ikkje')) {
        setError('Fann ikkje Mikkelchase-økta. Sjekk PIN-koden og prøv igjen.');
      } else if (msg.includes('avslutta')) {
        setError('Denne Mikkelchase-økta er allereie avslutta.');
      } else {
        setError('Klarte ikkje bli med. Prøv igjen.');
      }
      setLoading(false);
    }
  };

  // Step 1: PIN input
  if (step === 'pin') {
    return (
      <div className="relative min-h-screen w-full flex flex-col items-center justify-between overflow-hidden bg-gradient-to-br from-teal-600 to-emerald-700 font-display">
        <div className="absolute -top-6 left-[10%] w-14 h-14 bg-white/15 rounded-full blur-xl" />
        <div className="absolute top-[22%] right-[6%] w-16 h-16 bg-emerald-300/25 rounded-full blur-xl" />
        <div className="absolute bottom-[28%] left-[4%] w-20 h-20 bg-teal-300/15 rounded-full blur-2xl" />

        <header className="w-full flex flex-col items-center justify-center pt-16 pb-8 px-4 flex-1">
          <span className="material-symbols-outlined text-white/80 text-5xl mb-3">explore</span>
          <h1 className="text-white tracking-tight text-4xl md:text-5xl font-extrabold text-center drop-shadow-sm">
            Bli med i Mikkelchase
          </h1>
          <p className="text-white/70 text-lg mt-2 text-center">Skriv inn PIN-koden frå skjermen</p>
        </header>

        <main className="w-full max-w-[480px] px-6 pb-8 flex-1 flex flex-col justify-start items-center space-y-4">
          <div className="w-full bg-white/10 backdrop-blur-sm p-6 rounded-3xl shadow-2xl border border-white/15">
            <form className="flex flex-col gap-4 w-full" onSubmit={handlePinSubmit}>
              <label className="sr-only" htmlFor="rebus-pin">PIN-kode</label>
              <input
                type="text"
                id="rebus-pin"
                inputMode="numeric"
                value={pin}
                onChange={handlePinChange}
                placeholder="PIN-kode"
                className="block w-full text-center bg-white text-gray-900 placeholder:text-gray-400 rounded-full ghost-border focus:ring-4 focus:ring-white/30 h-16 text-2xl font-bold shadow-inner transition-all duration-200 tracking-widest"
                disabled={loading}
                autoComplete="off"
                autoFocus
              />

              {error && (
                <div className="text-white bg-red-500/25 border border-red-400/30 px-4 py-3 rounded-xl text-center font-medium">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full h-14 btn-gradient text-white text-lg font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group"
              >
                <span>Gå vidare</span>
                <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">arrow_forward</span>
              </button>
            </form>
          </div>
        </main>

        <footer className="w-full py-6 px-4 text-center">
          <p className="text-white/60 text-sm font-medium">
            Mikkel Kviss &mdash; Mikkelchase
          </p>
        </footer>
      </div>
    );
  }

  // Step 2: Team selection + member name
  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-between overflow-hidden bg-gradient-to-br from-teal-600 to-emerald-700 font-display">
      <div className="absolute -top-6 left-[10%] w-14 h-14 bg-white/15 rounded-full blur-xl" />
      <div className="absolute top-[22%] right-[6%] w-16 h-16 bg-emerald-300/25 rounded-full blur-xl" />

      <header className="w-full flex flex-col items-center justify-center pt-12 pb-6 px-4">
        <span className="material-symbols-outlined text-white/80 text-5xl mb-3">groups</span>
        <h1 className="text-white tracking-tight text-3xl md:text-4xl font-extrabold text-center drop-shadow-sm">
          Nesten klare!
        </h1>
        <p className="text-white/70 text-base mt-2 text-center">Vel lag og skriv inn namnet ditt</p>
      </header>

      <main className="w-full max-w-[480px] px-6 pb-8 flex-1 flex flex-col justify-start items-center space-y-4">
        <div className="w-full bg-white/10 backdrop-blur-sm p-6 rounded-3xl shadow-2xl border border-white/15">
          {loadingSession ? (
            <div className="flex flex-col items-center py-8">
              <span className="material-symbols-outlined text-white/60 text-3xl animate-spin mb-2">progress_activity</span>
              <p className="text-white/60 text-sm">Lastar...</p>
            </div>
          ) : (
            <form className="flex flex-col gap-4 w-full" onSubmit={handleNameSubmit}>
              {/* Team name — dropdown if predefined, otherwise free text */}
              <div>
                <label className="text-white/80 text-sm font-semibold mb-1.5 block" htmlFor="team-name">
                  {predefinedTeams.length > 0 ? 'Vel ditt lag' : 'Lagnamn'}
                </label>
                {predefinedTeams.length > 0 ? (
                  <select
                    id="team-name"
                    value={teamName}
                    onChange={(e) => { setTeamName(e.target.value); setError(''); }}
                    className="block w-full bg-white text-gray-900 rounded-xl ghost-border focus:ring-4 focus:ring-white/30 h-14 text-lg font-bold shadow-inner transition-all duration-200 px-4"
                    disabled={loading}
                  >
                    <option value="">— Vel ditt lag —</option>
                    {predefinedTeams.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    id="team-name"
                    value={teamName}
                    onChange={(e) => { if (e.target.value.length <= 30) { setTeamName(e.target.value); setError(''); } }}
                    placeholder="Skriv lagnamnet"
                    className="block w-full text-center bg-white text-gray-900 placeholder:text-gray-400 rounded-xl ghost-border focus:ring-4 focus:ring-white/30 h-14 text-lg font-bold shadow-inner transition-all duration-200"
                    disabled={loading}
                    autoComplete="off"
                    autoFocus
                  />
                )}
              </div>

              {/* Member name */}
              <div>
                <label className="text-white/80 text-sm font-semibold mb-1.5 block" htmlFor="member-name">
                  Ditt fornamn
                </label>
                <input
                  type="text"
                  id="member-name"
                  value={memberName}
                  onChange={(e) => { if (e.target.value.length <= 30) { setMemberName(e.target.value); setError(''); } }}
                  placeholder="Skriv ditt namn"
                  className="block w-full text-center bg-white text-gray-900 placeholder:text-gray-400 rounded-xl ghost-border focus:ring-4 focus:ring-white/30 h-14 text-lg font-bold shadow-inner transition-all duration-200"
                  disabled={loading}
                  autoComplete="off"
                  autoFocus={predefinedTeams.length > 0}
                />
              </div>

              {error && (
                <div className="text-white bg-red-500/25 border border-red-400/30 px-4 py-3 rounded-xl text-center font-medium">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white text-lg font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-50"
              >
                <span>{loading ? 'Koplar til...' : 'Bli med'}</span>
                {!loading && (
                  <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">login</span>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('pin');
                  setError('');
                  setPredefinedTeams([]);
                }}
                className="text-white/60 hover:text-white text-sm font-medium transition-colors"
              >
                ← Tilbake til PIN
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="w-full py-6 px-4 text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 inline-flex items-center gap-2">
          <span className="text-white/70 text-sm">Mikkelchase-økt</span>
          <span className="text-white font-bold">{pin}</span>
        </div>
      </footer>
    </div>
  );
}
